import { Injectable, Injector } from '@angular/core';
import { IndexedDbService } from '../IndexedDbService/indexed-db.service';
import { ProcessService } from 'src/app/features/process/services/process.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { from, Observable, of, throwError } from 'rxjs';
import { mergeMap, catchError, tap, map, finalize } from 'rxjs/operators';
import { Process } from '../../models/process.model';

@Injectable({
  providedIn: 'root',
})
export class SyncService {
  private processService!: ProcessService;

  constructor(
    private indexedDbService: IndexedDbService,
    private injector: Injector,
    private snackBar: MatSnackBar
  ) { }

  private initProcessService() {
    if (!this.processService) {
      this.processService = this.injector.get(ProcessService);
    }
  }

  // Sincronizar processos offline
  syncOfflineProcesses(): Observable<void> {
    this.initProcessService(); // Inicializa o ProcessService de forma atrasada
    return this.indexedDbService.getProcesses('offlineProcesses').pipe(
      mergeMap((offlineProcesses: Process[]) => {
        if (offlineProcesses.length === 0) {
          console.log('Nenhum processo offline para sincronizar.');
          return of(void 0);
        }

        const syncRequests = offlineProcesses.map((process) =>
          this.syncSingleProcess(process)
        );

        return from(syncRequests).pipe(
          mergeMap((syncResult) => syncResult, 5), // Limita a 5 requisições em paralelo
          finalize(() => console.log('Sincronização de processos concluída.'))
        );
      }),
      catchError((error) => {
        console.error('Erro durante a sincronização:', error);
        return throwError(() => new Error('Falha ao sincronizar processos.'));
      })
    );
  }


  // Sincronizar um único processo
  private syncSingleProcess(process: Process): Observable<void> {
    this.initProcessService(); // Inicializa o ProcessService de forma atrasada
    return this.processService.createProcess(process).pipe(
      tap(() => {
        console.log(`Processo ID: ${process.id} sincronizado.`);
        this.indexedDbService
          .removeProcess('offlineProcesses', process.id)
          .subscribe();
        this.snackBar.open(`Processo ${process.id} sincronizado com sucesso!`, 'Fechar', {
          duration: 3000,
        });
      }),
      map(() => void 0),
      catchError((error) => {
        console.error(`Erro ao sincronizar o processo ${process.id}:`, error);
        this.snackBar.open(`Erro ao sincronizar o processo ${process.id}.`, 'Fechar', {
          duration: 3000,
        });
        return of(void 0);
      })
    );
  }

  // Sincronização em lote de processos
  syncProcessesBatch(processes: Process[], batchSize: number = 10): Observable<void> {
    this.initProcessService(); // Inicializa o ProcessService de forma atrasada
    const batches = this.chunkArray(processes, batchSize);
    let failedBatches: Process[][] = []; // Lotes que falharam na sincronização

    return from(batches).pipe(
      mergeMap(
        (batch, index) =>
          this.processService.createBatch(batch).pipe(
            tap(() => console.log(`Lote ${index + 1}/${batches.length} sincronizado.`)),
            map(() => void 0),
            catchError((err) => {
              console.error(`Erro ao sincronizar o lote ${index + 1}/${batches.length}:`, err);
              failedBatches.push(batch);
              return of(void 0);
            })
          ),
        3 // Número de lotes a serem executados em paralelo
      ),
      finalize(() => {
        if (failedBatches.length > 0) {
          console.warn(`${failedBatches.length} lotes falharam. Retentando...`);
          this.retryFailedBatches(failedBatches);
        } else {
          console.log('Todos os lotes foram sincronizados com sucesso.');
        }
      })
    );
  }

  // Função para dividir um array em lotes menores
  private chunkArray<T>(array: T[], size: number): T[][] {
    return Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
      array.slice(i * size, i * size + size)
    );
  }


  // Retentar sincronização de lotes que falharam
  private retryFailedBatches(failedBatches: Process[][]): void {
    this.initProcessService(); // Inicializa o ProcessService de forma atrasada
    from(failedBatches)
      .pipe(
        mergeMap(
          (batch, index) =>
            this.processService.createBatch(batch).pipe(
              tap(() =>
                console.log(
                  `Lote de retentativa ${index + 1}/${failedBatches.length} sincronizado.`
                )
              ),
              catchError((err) => {
                console.error(`Erro ao retentar o lote ${index + 1}/${failedBatches.length}:`, err);
                return of(void 0);
              })
            ),
          3 // Número de lotes a serem executados em paralelo
        ),
        finalize(() => console.log('Retentativa de lotes concluída.'))
      )
      .subscribe();
  }
}